import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Filter, Users, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { jobAPI, candidateAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import CandidateCard from './CandidateCard';
import AddCandidateModal from './AddCandidateModal';

const PIPELINE_STAGES = [
  { key: 'sourced', label: 'Sourced', color: 'bg-gray-100' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'bg-blue-100' },
  { key: 'manager_review', label: 'Manager Round', color: 'bg-purple-100' },
  { key: 'manager_selected', label: 'Manager Selected', color: 'bg-purple-200' },
  { key: 'manager_rejected', label: 'Manager Rejected', color: 'bg-red-100' },
  { key: 'hod_review', label: 'HOD Round', color: 'bg-fuchsia-100' },
  { key: 'hod_selected', label: 'HOD Selected', color: 'bg-fuchsia-200' },
  { key: 'hod_rejected', label: 'HOD Rejected', color: 'bg-red-200' },
  { key: 'ctc_round', label: 'CTC Round', color: 'bg-yellow-100' },
  { key: 'offer_sent', label: 'Offer Sent', color: 'bg-indigo-100' },
  { key: 'accepted', label: 'Accepted', color: 'bg-green-200' },
  { key: 'declined', label: 'Declined', color: 'bg-red-200' },
  { key: 'joined', label: 'Joined', color: 'bg-green-300' }
];

const CandidatePipeline = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ['ta_executive', 'admin', 'superadmin'].includes(user?.role);

  const [job, setJob] = useState(null);
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [jobRes, pipelineRes] = await Promise.all([
        jobAPI.getJob(jobId),
        candidateAPI.getPipeline(jobId)
      ]);
      
      if (jobRes.status === 'success') {
        setJob(jobRes.data.job);
      }
      
      if (pipelineRes.status === 'success') {
        setPipeline(pipelineRes.data.pipeline || {});
      }
    } catch (err) {
      setError(err.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleCandidateAdded = () => {
    setShowAddModal(false);
    fetchData();
  };

  const getTATStatus = () => {
    if (!job?.deadline) return null;
    
    const now = new Date();
    const deadline = new Date(job.deadline);
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { label: `${Math.abs(daysLeft)} days overdue`, color: 'text-red-600', bgColor: 'bg-red-50', icon: AlertTriangle };
    } else if (daysLeft <= 3) {
      return { label: `${daysLeft} days left`, color: 'text-red-600', bgColor: 'bg-red-50', icon: AlertTriangle };
    } else if (daysLeft <= 7) {
      return { label: `${daysLeft} days left`, color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: Clock };
    }
    return { label: `${daysLeft} days left`, color: 'text-green-600', bgColor: 'bg-green-50', icon: Clock };
  };

  const filterCandidates = (candidates) => {
    if (!searchQuery.trim()) return candidates;
    
    const query = searchQuery.toLowerCase();
    return candidates.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.phone?.includes(query)
    );
  };

  const getTotalCandidates = () => {
    return Object.values(pipeline).reduce((sum, candidates) => sum + (candidates?.length || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/dashboard/recruitment')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          ← Back to Recruitment
        </button>
      </div>
    );
  }

  const tatStatus = getTATStatus();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard/recruitment')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{job?.title}</h1>
              <p className="text-sm text-gray-500">
                {job?.department} • {job?.positions} position(s)
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {tatStatus && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${tatStatus.bgColor}`}>
                <tatStatus.icon className={`w-4 h-4 ${tatStatus.color}`} />
                <span className={`text-sm font-medium ${tatStatus.color}`}>
                  {tatStatus.label}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">
                {getTotalCandidates()} Candidates
              </span>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Candidate
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search candidates by name, email, or phone..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 text-red-700">
          {error}
        </div>
      )}

      {/* Kanban Board */}
      <div className="p-6 overflow-x-auto">
        <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
          {PIPELINE_STAGES.map(stage => {
            const candidates = filterCandidates(pipeline[stage.key] || []);
            
            return (
              <div 
                key={stage.key} 
                className="w-72 flex-shrink-0"
              >
                {/* Stage Header */}
                <div className={`${stage.color} rounded-t-lg px-4 py-3 border border-gray-200 border-b-0`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">{stage.label}</h3>
                    <span className="px-2 py-0.5 bg-white rounded-full text-sm font-medium">
                      {candidates.length}
                    </span>
                  </div>
                </div>
                
                {/* Stage Content */}
                <div
                  className="bg-gray-100 rounded-b-lg border border-gray-200 border-t-0 p-3 min-h-[500px] max-h-[calc(100vh-280px)] overflow-y-auto"
                >
                  {candidates.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <p className="text-sm">No candidates</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {candidates.map(candidate => (
                        <CandidateCard
                          key={candidate._id}
                          candidate={candidate}
                          canManage={canManage}
                          currentStage={stage.key}
                          onSelect={() => setSelectedCandidate(candidate)}
                          onRefresh={fetchData}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Candidate Modal */}
      {showAddModal && (
        <AddCandidateModal
          jobId={jobId}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleCandidateAdded}
        />
      )}
    </div>
  );
};

export default CandidatePipeline;
